/**
 * THE SCREEN TABLE — every place the support assistant is allowed to name.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * The app has no route-constants module: `App.tsx` declares its paths as inline JSX and the
 * other 44 navigations are raw string literals. That is survivable for hand-written code,
 * because a developer who mistypes a path finds out immediately. It is NOT survivable for the
 * support assistant, which names screens from a corpus written months earlier and resolved by
 * a Function App that cannot see this repo at all.
 *
 * So this table is the one place that knows three things at once:
 *
 *   1. the path, spelled exactly as `App.tsx` spells it;
 *   2. what a user must BE to reach it;
 *   3. what to call it in a sentence.
 *
 * Everything the assistant can do with a screen — navigate to it, open a modal on it, walk
 * someone through it, offer a "?" for it — resolves through `screenFor`/`canReachScreen` here.
 * There is deliberately no second path.
 *
 * ⚠️ THIS FILE IS HALF OF A CROSS-REPO CONTRACT.
 * The Function App holds the other half in `Support/answer-actions.json`, and it holds only
 * KEYS — never paths. That asymmetry is the security property: a marker the model emits can
 * name a screen but cannot express a destination, exactly as `[[image:key]]` can name a
 * picture but not a URL. `supportActions.test.ts` restates the FA's key list and fails if the
 * two drift. See `AnswerActionGuard.cs`.
 *
 * ⚠️ DO NOT lower-case these paths. React-router v6 is case-sensitive, and `/Analytics` is
 * capitalised (`supportAssistantAccess.ts` carries the same warning). A lower-cased compare
 * silently never matches, which presents as "the assistant just never offers that screen".
 *
 * ⚠️ WHAT IS DELIBERATELY NOT HERE (2026-09-28). The barcode page, the four inspection-report
 * pages and the manager planning/quality screens (chart, allocations, analytical tool, Compare
 * BOM, Capacity Settings, Test Results, Calibrations, Toolset, Certifications) are discontinued
 * — `App.tsx` redirects their old paths to `/home`. And `/choose-organization` is live but
 * OFF-LIMITS to the assistant: CoSMo must never offer, tour or describe the org picker, even
 * for a CORP user. Leaving it out of this table is what does that — no chip resolves to it, no
 * "?" renders on it, and no screen context or control label from it reaches the Function App.
 * Do not add it back. (The shop-floor board's barcode SEARCH is a different feature and stays.)
 */

import { isAnalyticsEnabled } from "./analyticsAccess";
import { CAPABILITY_KEYS, CapabilityKey } from "../access/capabilities";

/**
 * What a user must be able to do to reach a screen: a capability from `GET api/me`, or
 * `"analytics"`.
 *
 * A capability key is the one the router asks for the same path: `<CapabilityRoute
 * capability="…">` in `App.tsx` for a settings screen, and `viewJobs` for a board screen, which
 * `PrivateRoute`'s Analytics-only confinement keys on. `routes.test.ts` reads `App.tsx` and
 * fails when the two differ.
 *
 * `"analytics"` is the one composite value: `runReports` AND the environment's Analytics switch,
 * as `AnalyticsRoute` has it. Every role holds `runReports` today, the Analytics-only user
 * included, for whom this is the only screen there is.
 */
export type ScreenAccess = CapabilityKey | "analytics";

export interface ScreenDescriptor {
  /** Stable key. This is what crosses the wire; the path never does. */
  readonly key: string;

  /**
   * The path exactly as `App.tsx` declares it, including `:params`.
   * Parameterised screens carry exactly one param and it is always the production order.
   */
  readonly path: string;

  /** True when `path` contains a `:param` that must be supplied to navigate. */
  readonly param?: "order" | "sequence";

  /** What the user must be able to do. Enforced by `canReachScreen`, never by the caller. */
  readonly access: ScreenAccess;

  /**
   * Whether this screen lives behind the Manager view toggle rather than a permission.
   *
   * `isManagerMode` is a VIEW, not a permission (`appSettings.isManagerMode`). A manager in
   * Shop Floor view still passes the screen's `CapabilityRoute`, but `layouts/Default` only
   * renders the Settings ribbon in Manager view, so they would land on the screen with no way
   * to move between its siblings. For these screens the runner turns the view on first and
   * SAYS SO.
   */
  readonly requiresManagerView?: boolean;

  /** How to refer to it in a sentence. Used in prompts and in chip labels. */
  readonly title: string;
}

/**
 * ACCESS, SCREEN BY SCREEN.
 *
 * Where a route in `App.tsx` carries a `CapabilityRoute`, this table names the same capability.
 *
 * Where it does not, the screen is a board screen: open to anyone who may view jobs, a Manager
 * or a Shop Floor user, and closed to an Analytics-only user, whom `PrivateRoute` keeps on
 * Analytics. `/hierarchical` is one such screen: it has no guard of its own and is reached from
 * the job page's "This PW has linked PW" link whenever an order has relations, as well as from
 * the Manager ribbon and table — so it is `viewJobs`, and needs no Manager view.
 *
 * ⚠️ THE OTHER HALF OF THIS TABLE IS THE FUNCTION APP'S. `Support/answer-actions.json` and its
 * `AnswerActionGuardTests` pin the same capability for each key, screen by screen, on the
 * server's side; `routes.test.ts` pins it entry by entry here. Change both together.
 */
const SCREENS: readonly ScreenDescriptor[] = Object.freeze([
  // ---- Board screens: anyone who may view jobs ----------------------------------------
  { key: "home", path: "/home", access: "viewJobs", title: "the home board" },
  { key: "job", path: "job/:jobNumber", param: "order", access: "viewJobs", title: "the production order details screen" },
  { key: "job-routing", path: "log/:jobNumber", param: "order", access: "viewJobs", title: "the resources list for a production order" },
  { key: "job-bom", path: "bom/:jobNumber", param: "order", access: "viewJobs", title: "the parts list for a production order" },
  { key: "job-images", path: "job-images/:jobNumber", param: "order", access: "viewJobs", title: "the images for a production order" },
  { key: "job-documents", path: "job-documents/:jobNumber", param: "order", access: "viewJobs", title: "the documents for a production order" },
  { key: "similar-jobs-images", path: "similar-jobs-images/", access: "viewJobs", title: "images from similar jobs" },
  { key: "images", path: "/images", access: "viewJobs", title: "the Images screen" },
  { key: "documents", path: "/documents", access: "viewJobs", title: "the Documents screen" },
  { key: "hierarchical", path: "/hierarchical", access: "viewJobs", title: "the hierarchy view" },

  // ---- Guarded in App.tsx by CapabilityRoute ------------------------------------------
  { key: "settings-system", path: "/system", access: "manageSettings", requiresManagerView: true, title: "System Settings" },
  { key: "settings-users", path: "/users", access: "viewUsers", requiresManagerView: true, title: "User Settings" },
  { key: "settings-operations", path: "/operations", access: "manageOperations", requiresManagerView: true, title: "Resource Settings" },
  // `/toolsets`, with the "s". The discontinued `/Toolset` still redirects to the home board,
  // and App.tsx declares its routes without `caseSensitive`, so that redirect takes `/toolset`
  // as well. A live screen spelled `/toolset` would collide with it.
  { key: "settings-toolsets", path: "/toolsets", access: "viewTools", requiresManagerView: true, title: "Tools and Toolsets" },

  // ---- Guarded in App.tsx by AnalyticsRoute (feature flag) -----------------------------
  { key: "analytics", path: "/Analytics", access: "analytics", title: "the Analytics screen" },
]);

/** Every screen key, for the contract tests and for exhaustiveness checks. */
export const SCREEN_KEYS: readonly string[] = Object.freeze(SCREENS.map((s) => s.key));

const BY_KEY: ReadonlyMap<string, ScreenDescriptor> = new Map(SCREENS.map((s) => [s.key, s]));

/** Look a screen up by key. Unknown keys return null — they never throw and never guess. */
export const screenFor = (key: string | null | undefined): ScreenDescriptor | null =>
  (key && BY_KEY.get(key)) || null;

export interface AccessContext {
  /**
   * What the user may do, from the access provider (`useAccess().can`). It is false for every
   * key while access is loading, after it failed, for an unusable account and after a refused
   * organisation switch, so no screen is reachable then. Never the role bits in the store.
   */
  readonly can: (capability: CapabilityKey) => boolean;
}

const isCapabilityKey = (value: string): value is CapabilityKey =>
  (CAPABILITY_KEYS as readonly string[]).includes(value);

/**
 * CAN THIS USER REACH THIS SCREEN?
 *
 * This is the single gate. Navigation, modal-opening, walkthroughs and the per-screen "?" all
 * ask this one question, so there is exactly one answer to keep correct.
 *
 * It mirrors the router rather than replacing it: `CapabilityRoute` still redirects anyone who
 * arrives another way. This exists so the assistant never OFFERS a door it knows is locked —
 * an offer that bounces the user to /home reads as the assistant being wrong, which is worse
 * than it having said nothing.
 */
export const canReachScreen = (screen: ScreenDescriptor | null, { can }: AccessContext): boolean => {
  if (!screen) return false;

  // Read as a string: a stored or mistyped value must still fall to the closed branch below.
  const access: string = screen.access;

  if (access === "analytics") {
    // Two conditions, and the switch is checked first because an environment without Analytics
    // has no such screen for ANYONE — mirroring `AnalyticsRoute` in App.tsx.
    // `isAnalyticsEnabled` is imported rather than re-implemented: it accepts ONLY "Y", and a
    // second copy here that also accepted "TRUE" would let the assistant offer a screen the
    // router then bounces.
    return isAnalyticsEnabled() && can("runReports");
  }

  if (isCapabilityKey(access)) return can(access);

  // An access value that is neither a capability nor "analytics" must fail CLOSED: the assistant
  // declines to mention that screen rather than waving everyone through.
  return false;
};

/**
 * Build the URL for a screen, filling its parameter if it has one.
 *
 * Returns null when the screen needs a parameter and none was supplied, or when the supplied
 * parameter does not survive encoding. Callers navigate only on a non-null result.
 *
 * ⚠️ The parameter is ALWAYS `encodeURIComponent`d. The Function App validates it against the
 * order-number shape before it ever reaches here, but that shape permits `/` and `.` — and a
 * `/` dropped raw into a path is a traversal primitive (`/job/45/../system`). Encoding here is
 * the second of the two independent defences; neither is sufficient alone.
 */
export const pathForScreen = (screen: ScreenDescriptor, param?: string | null): string | null => {
  if (!screen.param) return normalise(screen.path);

  const raw = (param ?? "").trim();
  if (!raw) return null;

  // encodeURIComponent turns `/`, `?`, `#` and every other separator into a percent escape, so
  // the result is always exactly one path segment. That is the whole defence — there is no
  // additional character check to make, and adding one would only invite the belief that the
  // encoding is optional.
  return normalise(screen.path.replace(/:[A-Za-z]+\??/, encodeURIComponent(raw)));
};

/**
 * `App.tsx` mixes leading-slash and relative paths; navigation wants a leading slash.
 *
 * A TRAILING slash is dropped as well, and that is not cosmetic: `similar-jobs-images/` is
 * declared with one in `App.tsx`, and without this `screenForPath("/similar-jobs-images")`
 * compared `/similar-jobs-images` against `/similar-jobs-images/` and matched nothing — the
 * screen existed in the table and could never be recognised. React-router v6 ignores trailing
 * slashes when it matches, so dropping it here also leaves `pathForScreen` producing a URL the
 * router resolves identically.
 */
const normalise = (path: string): string => {
  const withLeading = path.startsWith("/") ? path : `/${path}`;
  return withLeading.length > 1 ? withLeading.replace(/\/+$/, "") : withLeading;
};

/**
 * Which screen is the user on?
 *
 * Matches on the path pattern, not the component, because component names do not survive
 * minification and there is no page registry. Parameterised screens match on their static
 * prefix. Returns null on anything unmapped — including `/login`, `/logout`,
 * `termsandconditions`, `consent-details` and `/diagnostic`, none of which the assistant
 * should ever describe.
 */
export const screenForPath = (pathname: string): ScreenDescriptor | null => {
  const path = normalise((pathname || "").split("?")[0].replace(/\/+$/, "") || "/home");

  for (const screen of SCREENS) {
    const candidate = normalise(screen.path);

    if (!screen.param) {
      if (candidate === path) return screen;
      continue;
    }

    // `job/:jobNumber` → prefix `/job/`. An exact prefix match, so `/jobs` never matches `/job`.
    const prefix = candidate.slice(0, candidate.indexOf("/:") + 1);
    if (prefix.length > 1 && path.startsWith(prefix)) return screen;
  }

  return null;
};
